import { useEffect, useState } from 'react';
import SideNavBar from '../components/SideNavBar';
import { createItem, deleteItem, getErrorMessage, getItems, updateItem } from '../services/authApi';

function ItemsPage({ user, onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [formError, setFormError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    barcode: ''
  });

  const loadItems = async (page = 1, nextPerPage = perPage, nextSearch = search) => {
    try {
      setLoading(true);
      const data = await getItems({
        page,
        per_page: nextPerPage,
        search: nextSearch || undefined,
      });
      const list = Array.isArray(data) ? data : data?.data || [];
      setItems(list);

      if (!Array.isArray(data)) {
        setCurrentPage(data.current_page || 1);
        setLastPage(data.last_page || 1);
        setPerPage(data.per_page || nextPerPage);
        setTotal(data.total || list.length);
      } else {
        setCurrentPage(1);
        setLastPage(1);
        setTotal(list.length);
      }

      setPageError('');
    } catch (requestError) {
      setPageError(getErrorMessage(requestError, 'Failed to load items.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const applySearch = () => {
    loadItems(1, perPage, search);
  };

  const resetSearch = () => {
    setSearch('');
    loadItems(1, perPage, '');
  };

  const goToPage = (page) => {
    if (page < 1 || page > lastPage || page === currentPage) {
      return;
    }

    loadItems(page, perPage, search);
  };

  const handlePerPageChange = (value) => {
    const nextPerPage = Number(value);
    setPerPage(nextPerPage);
    loadItems(1, nextPerPage, search);
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({ name: '', price: '', barcode: '' });
    setFormError('');
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      price: String(item.price ?? ''),
      barcode: item.barcode || ''
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const closeModal = (forceClose = false) => {
    if (isSubmitting && !forceClose) {
      return;
    }

    setIsModalOpen(false);
    setEditingItem(null);
    setFormData({ name: '', price: '', barcode: '' });
    setFormError('');
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        price: Number(formData.price),
        barcode: formData.barcode.trim() || null
      };

      if (editingItem) {
        const updatedItem = await updateItem(editingItem.id, payload);
        setItems((prev) =>
          prev.map((current) => (current.id === editingItem.id ? updatedItem : current))
        );
      } else {
        const createdItem = await createItem(payload);
        setItems((prev) => [createdItem, ...prev]);
      }

      closeModal(true);
    } catch (requestError) {
      setFormError(
        getErrorMessage(
          requestError,
          editingItem ? 'Failed to update item.' : 'Failed to create item.'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (item) => {
    setDeletingItem(item);
    setDeleteError('');
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = (forceClose = false) => {
    if (isDeleting && !forceClose) {
      return;
    }

    setIsDeleteModalOpen(false);
    setDeletingItem(null);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteItem(deletingItem.id);
      setItems((prev) => prev.filter((current) => current.id !== deletingItem.id));
      closeDeleteModal(true);
    } catch (requestError) {
      setDeleteError(getErrorMessage(requestError, 'Failed to delete item.'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-maroon-50 to-maroon-100 md:flex-row">
      <SideNavBar user={user} onLogout={onLogout} />
      <div className="flex flex-1 p-4 md:p-6">
        <div className="w-full rounded-2xl bg-white p-4 md:p-6 shadow-xl shadow-maroon-900/10">
          <div className="mb-5 flex items-center justify-between">
            <h1 className="text-3xl font-bold text-maroon-900">Items</h1>
            <button
              type="button"
              onClick={openCreateModal}
              className="rounded-lg bg-maroon-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-maroon-800"
            >
              Create Item
            </button>
          </div>

          <div className="mb-4 flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search item id/name"
              className="w-full rounded-lg border border-maroon-200 px-3 py-2 text-sm outline-none ring-maroon-300 focus:ring-2"
            />
            <button
              type="button"
              onClick={applySearch}
              className="rounded-lg bg-maroon-700 px-4 py-2 text-sm font-semibold text-white hover:bg-maroon-800"
            >
              Search
            </button>
            <button
              type="button"
              onClick={resetSearch}
              className="rounded-lg border border-maroon-300 px-4 py-2 text-sm font-semibold text-maroon-800 hover:bg-maroon-50"
            >
              Reset
            </button>
          </div>

          {loading ? <p className="text-maroon-700">Loading items...</p> : null}
          {pageError ? <p className="font-medium text-red-600">{pageError}</p> : null}

          {!loading && !pageError ? (
            <div className="overflow-x-auto rounded-lg border border-maroon-100">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-maroon-50 text-maroon-900">
                  <tr>
                    <th className="px-4 py-3 font-semibold">ID</th>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Barcode</th>
                    <th className="px-4 py-3 font-semibold">Price</th>
                    <th className="px-4 py-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td className="px-4 py-3 text-maroon-700" colSpan={5}>
                        No items found.
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => (
                      <tr key={item.id || index} className="border-t border-maroon-100">
                        <td className="px-4 py-3 text-maroon-800">{index + 1}</td>
                        <td className="px-4 py-3 text-maroon-800">
                          {item.name || item.item_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-maroon-800">
                          {item.barcode || '-'}
                        </td>
                        <td className="px-4 py-3 text-maroon-800">
                          {item.price ?? item.selling_price ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-maroon-800">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(item)}
                              className="rounded-md bg-amber-500 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-600"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => openDeleteModal(item)}
                              className="rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : null}

          {!loading && !pageError ? (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-maroon-700">
                Showing page {currentPage} of {lastPage} ({total} records)
              </p>
              <div className="flex items-center gap-2">
                <label className="text-sm text-maroon-800" htmlFor="item_per_page">
                  Per page
                </label>
                <select
                  id="item_per_page"
                  value={perPage}
                  onChange={(event) => handlePerPageChange(event.target.value)}
                  className="rounded-lg border border-maroon-200 px-2 py-1 text-sm outline-none ring-maroon-300 focus:ring-2"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => goToPage(currentPage - 1)}
                  className="rounded-lg border border-maroon-300 px-3 py-1 text-sm font-semibold text-maroon-800 disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={currentPage >= lastPage}
                  onClick={() => goToPage(currentPage + 1)}
                  className="rounded-lg border border-maroon-300 px-3 py-1 text-sm font-semibold text-maroon-800 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {isModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="mb-4 text-2xl font-bold text-maroon-900">
              {editingItem ? 'Edit Item' : 'Create Item'}
            </h2>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <label className="text-sm font-medium text-maroon-900" htmlFor="item_name">
                Name
              </label>
              <input
                id="item_name"
                type="text"
                value={formData.name}
                onChange={(event) => handleFieldChange('name', event.target.value)}
                className="rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
                required
              />

              <label className="text-sm font-medium text-maroon-900" htmlFor="item_price">
                Price
              </label>
              <input
                id="item_price"
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(event) => handleFieldChange('price', event.target.value)}
                className="rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
                required
              />

              <label className="text-sm font-medium text-maroon-900" htmlFor="item_barcode">
                Barcode
              </label>
              <input
                id="item_barcode"
                type="text"
                value={formData.barcode}
                onChange={(event) => handleFieldChange('barcode', event.target.value)}
                className="rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
              />

              {formError ? <p className="text-sm font-medium text-red-600">{formError}</p> : null}

              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={isSubmitting}
                  className="rounded-lg border border-maroon-300 px-4 py-2 text-sm font-semibold text-maroon-800 hover:bg-maroon-50 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-lg bg-maroon-700 px-4 py-2 text-sm font-semibold text-white hover:bg-maroon-800 disabled:opacity-60"
                >
                  {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {isDeleteModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="mb-2 text-2xl font-bold text-maroon-900">Delete Item</h2>
            <p className="mb-4 text-sm text-maroon-700">
              Are you sure you want to delete "{deletingItem?.name || 'this item'}"?
            </p>

            {deleteError ? <p className="mb-3 text-sm font-medium text-red-600">{deleteError}</p> : null}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={isDeleting}
                className="rounded-lg border border-maroon-300 px-4 py-2 text-sm font-semibold text-maroon-800 hover:bg-maroon-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ItemsPage;
